import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import styles from "./ImprovementProjectDetails.module.scss";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import Textarea from "../../components/Textarea/Textarea";
import RaciAssignment from "../../components/RaciAssignment/RaciAssignment";
import PageLoader from "../../components/Loader/PageLoader";
import {
  ArrowLeft,
  Archive,
  Check,
  ChevronDown,
  Clock,
  FileText,
  MessageSquare,
  Paperclip,
  Save,
  Send,
  User,
} from "lucide-react";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import OutsideClickHandler from "react-outside-click-handler";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import DatePickerField from "../../components/FormikComponents/DatePickerField";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { handleExport } from "../../utils/helpers";
import ConfirmationPopup from "../../modals/ConfirmationPopup";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import Swal from "sweetalert2";
import FormikUpload from "../../components/FormikComponents/FormikUpload/FormikUpload";

const statusOptions = [
  { id: "Open", label: "Open", tone: "open" },
  { id: "In Progress", label: "In Progress", tone: "progress" },
  { id: "In Review", label: "In Review", tone: "review" },
  { id: "Done", label: "Done", tone: "done" },
];

const impactScores = { low: 1, medium: 2, high: 3 };
const getImpactScore = (level) => impactScores[level?.toLowerCase?.()] ?? 0;

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) {
    return `about ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

const formatDateValue = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0];
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split("T")[0];
  }
  return String(value);
};

const parseUpdateBody = (body) => {
  if (!body) return [];
  const rawText = typeof body === "string" ? body : String(body);
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith("- ") ? line.slice(2).trim() : line));

  return lines.map((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const label = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (label) {
        return { label, value };
      }
    }
    return { text: line };
  });
};

const getUpdateTimestamp = (update) =>
  update?.date ||
  update?.updated_at ||
  update?.updatedAt ||
  update?.updated_on ||
  update?.updatedOn ||
  update?.created_at ||
  update?.createdAt ||
  update?.created_on ||
  update?.createdOn;

const getUpdateBodyText = (update) => {
  if (!update?.body) return "";
  return typeof update.body === "string" ? update.body : String(update.body);
};

const getUpdateAuthor = (update) => {
  const candidate =
    update?.author || update?.user || update?.created_by || update?.createdBy;
  if (typeof candidate === "string") return candidate;
  if (candidate && typeof candidate === "object") {
    return candidate.email || candidate.name || "";
  }
  return "";
};

const normalizeUpdateAttachments = (attachments = []) => {
  const items = Array.isArray(attachments) ? attachments : [attachments];
  return items
    .map((attachment) => {
      if (!attachment) return null;
      if (typeof attachment === "string") {
        return {
          url: attachment,
          filename: attachment.split("/").pop() || "Attachment",
        };
      }
      const url = attachment.url || attachment.path || attachment.link || "";
      const filename =
        attachment.filename ||
        attachment.name ||
        (url ? url.split("/").pop() : "") ||
        "Attachment";
      return { url, filename };
    })
    .filter((attachment) => attachment && attachment.url);
};

const isStructuredUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (!body) return false;
  return parseUpdateBody(body).some((item) => item.label);
};

const isCommentUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (body) return !isStructuredUpdate(update);
  return normalizeUpdateAttachments(update?.attachments || []).length > 0;
};

const FormStateBridge = ({ dirty, isSubmitting, onChange }) => {
  React.useEffect(() => {
    onChange(dirty, isSubmitting);
  }, [dirty, isSubmitting, onChange]);

  return null;
};

const ImprovementProjectDetails = () => {
  const { orgId, improvementKey } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const location = useLocation();

  const { improvementsData, findingsData } = useSelector(
    (state) => state.organization,
  );
  const userEmail = useSelector((state) => state.user.email);

  useFetchOrganizationData();

  const improvement = useMemo(
    () =>
      Object.values(improvementsData || {}).find((item) => item.key === improvementKey),
    [improvementKey, improvementsData],
  );

  const hasLoadedImprovements = Object.keys(improvementsData || {}).length > 0;

  const [activeTab, setActiveTab] = useState("details");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [updates, setUpdates] = useState([]);
  const sessionToken = localStorage.getItem("session_token");

  const handleFormState = useCallback((dirty, submitting) => {
    setIsDirty(dirty);
    setIsSubmitting(submitting);
  }, []);

  const linkedFindingEntries = useMemo(() => {
    const linked = improvement?.linked_findings || [];
    return linked
      .map((item) => {
        const key =
          typeof item === "string" || typeof item === "number" ? item : item?.key;
        const data =
          (typeof item === "object" && item) ||
          findingsData?.[key] ||
          findingsData?.[item];
        return { key, data };
      })
      .filter((entry) => entry.key);
  }, [improvement, findingsData]);

  const associatedRisks = useMemo(() => {
    const seen = new Set();
    return linkedFindingEntries
      .map(({ data }) => data?.risk)
      .filter((risk) => risk && (risk.risk_id || risk.risk_num || risk.id))
      .filter((risk) => {
        const key = risk.risk_id || risk.risk_num || risk.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [linkedFindingEntries]);

  useEffect(() => {
    const nextUpdates = Array.isArray(improvement?.updates) ? improvement.updates : [];
    setUpdates(nextUpdates);
  }, [improvement?.updates]);

  if (!improvement && hasLoadedImprovements) {
    return (
      <MainLayout>
        <div className={styles.notFound}>
          <div className={styles.emptyState}>
            We could not find this improvement project.
          </div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/improvement-projects${location.search}`)}
          >
            <ArrowLeft size={18} />
            Back to Improvements
          </button>
        </div>
      </MainLayout>
    );
  }

  if (!improvement) {
    return (
      <MainLayout>
        <PageLoader text="Loading improvement project..." pageLoader />
      </MainLayout>
    );
  }

  const lastUpdatedAt = updates.reduce((latest, update) => {
    const candidate = getUpdateTimestamp(update);
    if (!candidate) return latest;
    if (!latest) return candidate;
    const candidateTime = new Date(candidate).getTime();
    const latestTime = new Date(latest).getTime();
    if (Number.isNaN(candidateTime)) return latest;
    if (Number.isNaN(latestTime) || candidateTime > latestTime) return candidate;
    return latest;
  }, "");
  const showMetaCard = Boolean(lastUpdatedAt);

  const initialValues = {
    name: improvement.name || "",
    description: improvement.description || "",
    date: improvement.date ? new Date(improvement.date) : "",
    responsible: Array.isArray(improvement.responsible)
      ? improvement.responsible[0] || ""
      : improvement.responsible || "",
    accountable: normalizeArray(improvement.accountable),
    consulted: normalizeArray(improvement.consulted),
    informed: normalizeArray(improvement.informed),
    status: improvement.status || "Open",
  };

  const handleSave = async (values, formikHelpers) => {
    const payload = {};

    if ((improvement.name || "") !== values.name) {
      payload.name = values.name;
    }
    if ((improvement.description || "") !== values.description) {
      payload.description = values.description;
    }
    if ((improvement.status || "") !== values.status) {
      payload.status = values.status;
    }

    const currentResponsible = Array.isArray(improvement.responsible)
      ? improvement.responsible[0] || ""
      : improvement.responsible || "";
    const cleanedResponsible = values.responsible?.trim?.() || "";
    if (currentResponsible !== cleanedResponsible) {
      payload.responsible = cleanedResponsible;
    }

    ["accountable", "consulted", "informed"].forEach((field) => {
      const current = normalizeArray(improvement[field]);
      const next = normalizeArray(values[field])
        .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
        .filter(Boolean);
      if (JSON.stringify(current) !== JSON.stringify(next)) {
        payload[field] = next;
      }
    });

    const nextDate = formatDateValue(values.date);
    const currentDate = formatDateValue(improvement.date);
    if (nextDate !== currentDate) {
      payload.date = nextDate;
    }

    if (Object.keys(payload).length === 0) {
      formikHelpers.setSubmitting(false);
      return;
    }

    const requestBody = [
      {
        id: improvement.id,
        ...payload,
      },
    ];

    try {
      await axiosInstance.patch(`/nocodb/improvements?org_id=${orgId}`, requestBody, {
        headers: {
          "Content-Type": "application/json",
          "session-token": sessionToken,
        },
      });

      if (values.status === "Done") {
        const linkedFindings = linkedFindingEntries
          .map((entry) => entry.data)
          .filter((entry) => entry?.id);

        if (linkedFindings.length > 0) {
          await Promise.all(
            linkedFindings.map((finding) =>
              axiosInstance.patch(
                `/nocodb/findings?org_id=${orgId}`,
                [{ id: finding.id, status: "Done" }],
                {
                  headers: {
                    "Content-Type": "application/json",
                    "session-token": sessionToken,
                  },
                },
              ),
            ),
          );
        }
      }

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          false,
          false,
          false,
        ),
      );
    } catch (error) {
      console.error("Failed to save improvement project", error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (
    values,
    { resetForm, setFieldValue, setSubmitting },
  ) => {
    try {
      const requestBody = {
        body: values.body,
        attachments: (values.attachments || []).map((attachment) => attachment),
        improvement: improvement?.id || 0,
        finding: 0,
        audit_task: 0,
      };

      const response = await axiosInstance.post(
        `/nocodb/update?org_id=${orgId}`,
        requestBody,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        const transformedAttachments = normalizeUpdateAttachments(
          values.attachments || [],
        );
        setUpdates((prevUpdates) => [
          {
            author: userEmail,
            date: new Date().toISOString(),
            body: values.body,
            attachments: transformedAttachments,
          },
          ...prevUpdates,
        ]);
        setFieldValue("attachments", []);

        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            null,
            false,
            true,
            true,
            false,
            false,
            false,
          ),
        );

        resetForm();
      }
    } catch (error) {
      console.error("Error adding update:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    setIsArchiving(true);

    try {
      await axiosInstance.patch(
        `/nocodb/improvements?org_id=${orgId}`,
        [{ id: improvement.id, archived: true }],
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          true,
          false,
          false,
          false,
        ),
      );

      Swal.fire({
        toast: true,
        icon: "success",
        title: "Archive successful",
        text: `Keys archived: ${improvement.key}`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      navigate(`/${orgId}/improvement-projects${location.search}`, { replace: true });
    } catch (error) {
      console.error("Error archiving improvement", error);
      Swal.fire({
        toast: true,
        icon: "error",
        title: "Archive failed",
        text: "Could not archive improvement project. Please try again.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleExportWord = async () => {
    if (isExporting) return;
    setIsExporting(true);
    await handleExport({
      selectedItems: [improvement],
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Improvement Project",
    });
    setIsExporting(false);
  };

  const getStatusClassName = (status) => {
    switch (status) {
      case "Open":
        return "openStatus";
      case "In Progress":
        return "inProgressStatus";
      case "In Review":
        return "inReviewStatus";
      case "Done":
        return "resolvedStatus";
      default:
        return "";
    }
  };

  const renderTabContent = (tab, values) => {
    if (tab === "improvement-report") {
      return <div className={styles.emptyState}>Coming soon.</div>;
    }

    if (tab === "comments") {
      const commentEntries = updates
        .filter(isCommentUpdate)
        .map((update) => {
          const attachments = normalizeUpdateAttachments(
            update?.attachments || [],
          );
          return {
            date: getUpdateTimestamp(update),
            author: getUpdateAuthor(update),
            body: getUpdateBodyText(update).trim(),
            attachments,
          };
        })
        .filter((entry) => entry.body || entry.attachments.length)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!commentEntries.length) {
        return <div className={styles.emptyState}>No comments yet.</div>;
      }

      return (
        <div className={styles.commentsCard}>
          <div className={styles.commentsHeader}>
            <div className={styles.commentsTitleRow}>
              <MessageSquare size={20} />
              <div className={styles.commentsTitle}>Comments</div>
            </div>
            <div className={styles.commentsSubtitle}>
              Notes and attachments added by the team
            </div>
          </div>
          <div className={styles.commentsList}>
            {commentEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const author = entry.author || "Unknown";

              return (
                <div key={`${author}-${entry.date || idx}`} className={styles.commentItem}>
                  <div className={styles.commentMetaRow}>
                    <div className={styles.commentAuthor}>{author}</div>
                    {relative && <div className={styles.commentTime}>{relative}</div>}
                  </div>
                  {timestamp && (
                    <div className={styles.commentTimestamp}>{timestamp}</div>
                  )}
                  {entry.body && (
                    <div className={styles.commentBody}>{entry.body}</div>
                  )}
                  {entry.attachments.length > 0 && (
                    <div className={styles.commentAttachments}>
                      {entry.attachments.map((attachment, attachmentIndex) => (
                        <a
                          key={`${attachment.url}-${attachmentIndex}`}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.commentAttachment}
                        >
                          <Paperclip size={14} />
                          <span>{attachment.filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (tab === "change-history") {
      const historyEntries = updates
        .filter(isStructuredUpdate)
        .map((update) => ({
          type: "Updated",
          date: getUpdateTimestamp(update),
          author: getUpdateAuthor(update),
          body: getUpdateBodyText(update) || "Updated record",
        }));

      const sortedEntries = historyEntries
        .filter((entry) => entry.date || entry.author || entry.body)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!sortedEntries.length) {
        return <div className={styles.emptyState}>No changes yet.</div>;
      }
      return (
        <div className={styles.changeHistoryCard}>
          <div className={styles.changeHistoryHeader}>
            <div className={styles.changeHistoryTitleRow}>
              <Clock size={20} />
              <div className={styles.changeHistoryTitle}>Change History</div>
            </div>
            <div className={styles.changeHistorySubtitle}>
              Track of all changes made to this record
            </div>
          </div>
          <div className={styles.changeHistoryList}>
            {sortedEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const rawBody =
                entry.body && typeof entry.body !== "string"
                  ? String(entry.body)
                  : entry.body || "";
              const parsedBody = rawBody ? parseUpdateBody(rawBody) : [];
              const hasStructuredBody =
                parsedBody.length > 1 || parsedBody.some((item) => item.label);
              return (
                <div
                  key={`${entry.type}-${idx}`}
                  className={classNames(styles.changeHistoryItem, styles.historyUpdated)}
                >
                  <div className={styles.changeHistoryMetaRow}>
                    <div className={styles.changeHistoryMetaLeft}>
                      <div
                        className={classNames(
                          styles.changeHistoryBadge,
                          styles.badgeUpdated,
                        )}
                      >
                        {entry.type}
                      </div>
                      {relative && (
                        <span className={styles.changeHistoryRelative}>
                          {relative}
                        </span>
                      )}
                    </div>
                    {timestamp && (
                      <span className={styles.changeHistoryTimestamp}>
                        {timestamp}
                      </span>
                    )}
                  </div>
                  {(entry.author || entry.body) && (
                    <div className={styles.changeHistoryDetails}>
                      {entry.author && (
                        <div className={styles.changeHistoryAuthor}>
                          <User size={14} />
                          <span>{entry.author}</span>
                        </div>
                      )}
                      {entry.body && (
                        <div className={styles.changeHistoryBody}>
                          {!hasStructuredBody ? (
                            <span className={styles.changeHistoryBodyText}>
                              {rawBody}
                            </span>
                          ) : (
                            <ul className={styles.changeHistoryBodyList}>
                              {parsedBody.map((item, itemIndex) => (
                                <li
                                  key={`${entry.type}-${idx}-${itemIndex}`}
                                  className={styles.changeHistoryBodyItem}
                                >
                                  {item.label ? (
                                    <>
                                      <span className={styles.changeHistoryBodyLabel}>
                                        {item.label}
                                      </span>
                                      <span className={styles.changeHistoryBodyValue}>
                                        {item.value || "Not set"}
                                      </span>
                                    </>
                                  ) : (
                                    <span className={styles.changeHistoryBodyText}>
                                      {item.text}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.detailsGrid}>
        <div className={styles.fieldBlock}>
          <label htmlFor="name">Title</label>
          <Field name="name" component={Input} placeholder="Title" />
        </div>

        <div className={classNames(styles.fieldBlock, styles.fullWidth)}>
          <label htmlFor="description">Description</label>
          <Field name="description" component={Textarea} placeholder="Description" />
        </div>

        <div className={styles.fieldBlock}>
          <label htmlFor="date">Date</label>
          <Field name="date" component={DatePickerField} />
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.topBar}>
          <Button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/improvement-projects${location.search}`)}
            text="Back to Improvements"
            icon={<ArrowLeft size={16} />}
            iconPosition="left"
          />

          <div className={styles.actions}>
            <Button
              text={isExporting ? "Exporting..." : "Word"}
              className={styles.secondaryAction}
              icon={<FileText size={16} />}
              iconPosition="left"
              onClick={handleExportWord}
              disabled={isExporting}
            />
            <Button
              text={isArchiving ? "Archiving..." : "Archive"}
              className={styles.secondaryAction}
              icon={<Archive size={16} />}
              iconPosition="left"
              onClick={() => setShowArchiveConfirm(true)}
              disabled={isArchiving}
            />
            <Button
              text="Save Changes"
              className={styles.primaryAction}
              onClick={() => formikRef.current?.submitForm()}
              disabled={isSubmitting || !isDirty}
              icon={<Save size={16} />}
              iconPosition="left"
            />
          </div>
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values, helpers) => {
            helpers.setSubmitting(true);
            handleSave(values, helpers);
          }}
        >
          {({ values, dirty, isSubmitting: submitting }) => {
            const statusTone =
              statusOptions.find((option) => option.id === values.status)?.tone || "open";

            return (
              <Form className={styles.form}>
                <FormStateBridge
                  dirty={dirty}
                  isSubmitting={submitting}
                  onChange={handleFormState}
                />
                <div className={styles.headerCard}>
                  <div className={styles.headerTop}>
                    <div className={styles.keyPill}>{improvement.key}</div>
                    <div className={styles.statusBlock}>
                      <div className={styles.statusLabel}>Status</div>
                      <OutsideClickHandler
                        onOutsideClick={() => setIsStatusOpen(false)}
                        disabled={!isStatusOpen}
                      >
                        <div className={styles.statusDropdown}>
                          <button
                            type="button"
                            className={classNames(
                              styles.statusSelect,
                              styles[`status-${statusTone}`],
                            )}
                            onClick={() => setIsStatusOpen((prev) => !prev)}
                          >
                            <span>{values.status}</span>
                            <ChevronDown size={16} />
                          </button>
                          {isStatusOpen && (
                            <div className={styles.statusMenu}>
                              {statusOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className={classNames(styles.statusOption, {
                                    [styles.activeStatus]: values.status === option.id,
                                  })}
                                  onClick={() => {
                                    formikRef.current?.setFieldValue("status", option.id);
                                    setIsStatusOpen(false);
                                  }}
                                >
                                  <span className={styles.statusCheckSlot}>
                                    {values.status === option.id && <Check size={14} />}
                                  </span>
                                  <div
                                    className={classNames(
                                      styles.statusDot,
                                      styles[`dot-${option.tone}`],
                                    )}
                                  />
                                  <span className={styles.statusOptionLabel}>
                                    {option.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </OutsideClickHandler>
                    </div>
                  </div>
                </div>

                <div className={styles.bodyGrid}>
                  <div className={styles.raciSection}>
                    <div className={styles.sectionLabel}>RACI assignment</div>
                    <RaciAssignment />
                  </div>

                  <div className={styles.mainColumn}>
                    <div className={styles.tabHeaderRow}>
                      <div className={styles.tabs}>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "details",
                          })}
                          onClick={() => setActiveTab("details")}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "improvement-report",
                          })}
                          onClick={() => setActiveTab("improvement-report")}
                        >
                          Improvement Report
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "change-history",
                          })}
                          onClick={() => setActiveTab("change-history")}
                        >
                          Change History
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "comments",
                          })}
                          onClick={() => setActiveTab("comments")}
                        >
                          Comments
                        </button>
                      </div>
                    </div>

                    <div className={styles.tabPanel}>
                      {renderTabContent(activeTab, values)}
                    </div>
                  </div>

                  <div className={styles.sideColumn}>
                    <div className={styles.communicationCard}>
                      <div className={styles.communicationHeader}>
                        <div>
                          <div className={styles.communicationTitle}>
                            Communication Module
                          </div>
                          <div className={styles.communicationSubtitle}>
                            Add comments for this improvement.
                          </div>
                        </div>
                      </div>
                      <Formik
                        initialValues={{ body: "", attachments: [] }}
                        onSubmit={handleUpdateSubmit}
                      >
                        {({ values, isSubmitting, submitForm }) => (
                          <div className={styles.communicationForm}>
                            {userEmail && (
                              <div className={styles.communicationUser}>
                                {userEmail}
                              </div>
                            )}
                            <Field
                              name="body"
                              component={Textarea}
                              minHeight="90px"
                              className={styles.communicationTextarea}
                              placeholder="Add a comment..."
                            />
                            <Field
                              name="attachments"
                              component={FormikUpload}
                              greenIcon
                              className={styles.communicationUpload}
                              labelClassName={styles.communicationUploadLabel}
                              labelContent={
                                <span className={styles.communicationUploadContent}>
                                  <Paperclip
                                    size={14}
                                    className={styles.communicationUploadIcon}
                                  />
                                  <span className={styles.communicationUploadText}>
                                    Attach files
                                  </span>
                                  <span className={styles.communicationUploadHint}>
                                    PDF, PNG, DOC
                                  </span>
                                </span>
                              }
                            />
                            <Button
                              text={isSubmitting ? "Posting..." : "Post comment"}
                              className={styles.communicationSubmit}
                              icon={<Send size={14} />}
                              iconPosition="right"
                              type="button"
                              onClick={submitForm}
                              disabled={
                                isSubmitting ||
                                (!values.body.trim() &&
                                  (values.attachments || []).length === 0)
                              }
                            />
                          </div>
                        )}
                      </Formik>
                    </div>
                    <div className={styles.sideCard}>
                      <div className={styles.sideCardHeader}>Associated Risks</div>
                      {associatedRisks.length ? (
                        <div className={styles.riskList}>
                          {associatedRisks.map((risk) => {
                            const riskKey = risk.risk_id || risk.risk_num || risk.id;
                            const level = String(risk.level || "").toLowerCase();
                            const levelKey = ["low", "medium", "high"].includes(level)
                              ? level
                              : "unknown";
                            const riskName =
                              risk.qms_id && risk.qms_description
                                ? `${risk.qms_id} - ${risk.qms_description}`
                                : risk.description || risk.qms_description || riskKey;
                            const description = risk.description || risk.qms_description || "";
                            const impactScore = getImpactScore(level);
                            const impactLabel = impactScore || "-";

                            return (
                              <div key={riskKey} className={styles.riskItem}>
                                <div className={styles.riskHeaderRow}>
                                  <div className={styles.riskId}>{riskKey}</div>
                                  {(level || levelKey) && (
                                    <span
                                      className={classNames(
                                        styles.riskBadge,
                                        styles[`risk-${levelKey}`],
                                      )}
                                    >
                                      {level || "unknown"}
                                    </span>
                                  )}
                                </div>
                                <div className={styles.riskName}>{riskName}</div>
                                {description && (
                                  <div className={styles.riskDescription}>{description}</div>
                                )}
                                <div className={styles.riskDivider} />
                                <div className={styles.riskMetaRow}>
                                  <div className={styles.riskMetaItem}>
                                    <div className={styles.riskMetaLabel}>Impact</div>
                                    <div className={styles.riskMetaValue}>{impactLabel}</div>
                                  </div>
                                </div>
                                <Button
                                  text="View Risk Details"
                                  className={styles.riskAction}
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/${orgId}/risk-analysis?risk=${encodeURIComponent(
                                        riskKey,
                                      )}`,
                                    )
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className={styles.emptyState}>No associated risks yet.</div>
                      )}
                      {associatedRisks.length > 0 && (
                        <div className={styles.associatedRisksCount}>
                          {associatedRisks.length} risk
                          {associatedRisks.length === 1 ? "" : "s"} from linked findings
                        </div>
                      )}
                    </div>
                    <div className={styles.sideCard}>
                      <div className={styles.sideCardHeader}>Linked Findings</div>
                      {linkedFindingEntries.length ? (
                        <>
                          <div className={styles.linkedFindingsList}>
                            {linkedFindingEntries.map(({ key, data }) => {
                              const status = data?.status;
                              const description =
                                data?.description || data?.reference || "No description";
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={styles.linkedFindingButton}
                                  onClick={() =>
                                    navigate(
                                      `/${orgId}/findings/${key}${location.search}`,
                                    )
                                  }
                                >
                                  <div className={styles.linkedFindingHeader}>
                                    <div className={styles.linkedFindingKey}>{key}</div>
                                    {status && (
                                      <span
                                        className={classNames(
                                          "statusCell",
                                          getStatusClassName(status),
                                        )}
                                      >
                                        {status}
                                      </span>
                                    )}
                                  </div>
                                  <div className={styles.linkedFindingDescription}>
                                    {description}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <div className={styles.linkedFindingsCount}>
                            {linkedFindingEntries.length} finding
                            {linkedFindingEntries.length === 1 ? "" : "s"} linked
                          </div>
                        </>
                      ) : (
                        <div className={styles.emptyState}>No linked findings yet.</div>
                      )}
                    </div>
                    {showMetaCard && (
                      <div className={classNames(styles.sideCard, styles.metaCard)}>
                        <div className={styles.metaGrid}>
                          <div>
                            <div className={styles.metaLabel}>Last updated</div>
                            <div className={styles.metaValue}>
                              {lastUpdatedAt ? formatDateTime(lastUpdatedAt) : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      {showArchiveConfirm && (
        <ConfirmationPopup
          message="Are you sure you want to archive this improvement project?"
          illustration={ArchiveIllustration}
          confirmText="Yes, archive"
          onConfirm={handleArchive}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}
    </MainLayout>
  );
};

export default ImprovementProjectDetails;
